import React, { useState } from 'react';
import '../styles/BottomConvertCalculator.css';

interface Price {
  code: string;
  bid: number;
  ask: number;
  category: string;
  source: string;
}

interface BottomConvertCalculatorProps {
  prices: Price[];
}

const BottomConvertCalculator: React.FC<BottomConvertCalculatorProps> = ({ prices }) => {
  const [from, setFrom] = useState<string>('USDTRY');
  const [to, setTo] = useState<string>('TRY');
  const [amount, setAmount] = useState('1');

  // Get unique currency codes from prices
  const getCurrencyList = () => {
    // Manuel olarak eklemek istediğiniz para birimleri
    const manualCurrencies = ['USDTRY', 'EURTRY', 'TRY','GBPTRY','CHFTRY','JPYTRY','TRYJPY','GBPTRY','CADTRY','AUDTRY','NZDTRY','CNYTRY','SEKTRY','NOKTRY','TRYTRY','RUBTRY','INRTRY','MXNTRY','ZARTRY','BRLTRY','CNYTRY','SEKTRY','NOKTRY','TRYTRY','RUBTRY','INRTRY','MXNTRY','ZARTRY','BRLTRY','AEDTRY','DKKTRY','SARTRY','QARTRY', 'GÜM', 'KG', 'ALTIN ONS', 'CHF', 'JPY', 'EUR/KG', 'USD/KG', 'HAS ALTIN','GÜM/USD',
       'GÜM/EUR', 'EUR/USD', 'ESKİ ÇEYREK', 'ÇEYREK', 'ESKİ YARIM', 'ESKİ YARIM', 'ESKİ TAM', 'TAM', 'ESKİ GREMSE', 'GREMSE','ESKİ ZIYNET 5', 'ZIYNET 5',
        'ESKİ ATA', 'ATA', 'ESKİ ATA 5', 'ATA 5', 'REŞAT LİRA', 'RESAT5',
        '1 GRAM', '5 GRAM', '10 GRAM','20 GRAM','50 GRAM','100 GRAM','200 GRAM','500 GRAM',
       '50 GR. GÜMÜŞ', '100 GR. GÜMÜŞ', '250 GR. GÜMÜŞ', '500 GR. GÜMÜŞ', '1000 GR. GÜMÜŞ'];
    const currencies = new Set<string>(manualCurrencies);
    prices.forEach(price => {
      // Sadece belirli kategorilerdeki kodlardan ekle
      if (
        ['DOVIZ', 'SARRAFIYE', 'MADEN', 'PARITE'].includes(price.category) &&
        price.code.includes('/')
      ) {
        const [fromCurrency, toCurrency] = price.code.split('/');
        if (fromCurrency && fromCurrency.trim().length > 0) {
          currencies.add(fromCurrency.trim());
        }
        if (toCurrency && toCurrency.trim().length > 0) {
          currencies.add(toCurrency.trim());
        }
      }
    });
    return Array.from(currencies).sort();
  };

  const handleSwap = () => {
    setFrom(to);
    setTo(from);
  };

  const getResult = () => {
    const val = parseFloat(amount) || 0;
    if (from === to) return val;

    // Tekil fiyatı olan ürünler için otomatik TRY dönüşümü
    const singlePrice = prices.find(p => p.code === from);
    if (singlePrice && to === 'TRY') {
      return (val * singlePrice.ask).toFixed(4);
    }
    const singlePriceReverse = prices.find(p => p.code === to);
    if (singlePriceReverse && from === 'TRY') {
      return (val / singlePriceReverse.bid).toFixed(4);
    }

    // HAS ALTIN özel durumu
    if ((from === 'HAS ALTIN' && to === 'TRY') || (from === 'TRY' && to === 'HAS ALTIN')) {
      const hasAltin = prices.find(p => p.code === 'HAS ALTIN');
      if (hasAltin) {
        if (from === 'HAS ALTIN' && to === 'TRY') {
          return (val * hasAltin.ask).toFixed(4);
        } else if (from === 'TRY' && to === 'HAS ALTIN') {
          return (val / hasAltin.bid).toFixed(4);
        }
      }
    }

    // 1. Doğrudan oran
    const directRate = prices.find(p => p.code === `${from}/${to}`);
    if (directRate) {
      return (val * directRate.ask).toFixed(4);
    }

    // 2. Ters oran
    const inverseRate = prices.find(p => p.code === `${to}/${from}`);
    if (inverseRate) {
      return (val / inverseRate.bid).toFixed(4);
    }

    // 3. Ortak ara para birimi ile çapraz kur
    // Tüm unique para birimlerini bul
    const allCurrencies = new Set<string>();
    prices.forEach(p => {
      if (p.code.includes('/')) {
        const [a, b] = p.code.split('/');
        allCurrencies.add(a.trim());
        allCurrencies.add(b.trim());
      }
    });

    for (let mid of allCurrencies) {
      if (mid === from || mid === to) continue;
      // from -> mid
      const fromToMid = prices.find(p => p.code === `${from}/${mid}`);
      // mid -> to
      const midToTo = prices.find(p => p.code === `${mid}/${to}`);
      if (fromToMid && midToTo) {
        return (val * fromToMid.ask * midToTo.ask).toFixed(4);
      }
      // Tersleri de kontrol et
      const midToFrom = prices.find(p => p.code === `${mid}/${from}`);
      if (midToFrom && midToTo) {
        return (val / midToFrom.bid * midToTo.ask).toFixed(4);
      }
      const fromToMidInv = prices.find(p => p.code === `${from}/${mid}`);
      const toToMid = prices.find(p => p.code === `${to}/${mid}`);
      if (fromToMidInv && toToMid) {
        return (val * fromToMidInv.ask / toToMid.bid).toFixed(4);
      }
      const midToFromInv = prices.find(p => p.code === `${mid}/${from}`);
      const toToMidInv = prices.find(p => p.code === `${to}/${mid}`);
      if (midToFromInv && toToMidInv) {
        return (val / midToFromInv.bid / toToMidInv.bid).toFixed(4);
      }
    }

    return 'Veri yok';
  };

  return (
    <div className="bottom-convert-calculator">
      <h3>Döviz Çevirici</h3>
      <div className="inputs-row">
        <input
          type="number"
          min="0"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          className="amount-input"
        />
        <select value={from} onChange={e => setFrom(e.target.value)}>
          {getCurrencyList().map(currency => (
            <option key={currency} value={currency}>{currency}</option>
          ))}
        </select>
        <button className="swap-btn" onClick={handleSwap} title="Değiştir">⇄</button>
        <select value={to} onChange={e => setTo(e.target.value)}>
          {getCurrencyList().map(currency => (
            <option key={currency} value={currency}>{currency}</option>
          ))}
        </select>
      </div>
      <div className="result-row">
        <span className="result-label">Sonuç:</span>
        <span className="result-value">{getResult()} {to}</span>
      </div>
    </div>
  );
};

export default BottomConvertCalculator; 